import { useAuth0 } from '@auth0/auth0-react'
import { AxiosProvider } from '@pubstack/http/AxiosContext'
import { create, AxiosInstance } from 'axios'
import { useAtomValue } from 'jotai'
import { ReactNode, useEffect, useMemo } from 'react'
import { CachePolicies, Provider } from 'use-http'
import { IncomingOptions } from 'use-http/dist/cjs/types'
import { useUser } from '~/auth/user.hooks'
import { configurationState } from '~/state'

export let api: AxiosInstance
// Freshest auth, read at request time (updated in an effect), so the single
// interceptor reflects scope/token changes.
const latestAuth = { getAccessTokenSilently: async (): Promise<string | undefined> => '', scopeId: '' }

export const HttpProvider = ({ children }: { children: ReactNode }) => {
  const { getAccessTokenSilently } = useAuth0()
  const appConfig = useAtomValue(configurationState)
  const user = useUser()

  const options = useMemo<IncomingOptions>(
    () => ({
      cachePolicy: CachePolicies.NO_CACHE,
      interceptors: {
        request: async ({ options }) => {
          const headers = new Headers(options.headers)
          const accessToken = await getAccessTokenSilently()
          headers.append('x-scope-id', user?.currentScopeId || '')
          headers.append('Authorization', `Bearer ${accessToken}`)
          return {
            ...options,
            headers,
          }
        },
      },
    }),
    [getAccessTokenSilently, user, user?.currentScopeId]
  )

  // Exported axios singleton: instance + interceptor created ONCE (avoids stacking
  // a new interceptor on every render). The global is assigned once; auth is read
  // at request time via `latestAuth`.
  if (!api) {
    /* oxlint-disable react/globals, react/immutability -- one-time init of the exported axios singleton; the global write and latestAuth seeding happen once, outside the compiler's memoization */
    latestAuth.getAccessTokenSilently = getAccessTokenSilently
    latestAuth.scopeId = user?.currentScopeId || ''
    api = create({ baseURL: appConfig.BACKEND_ENDPOINT })
    api.interceptors.request.use(
      async (config) => {
        config.headers['Authorization'] = `Bearer ${await latestAuth.getAccessTokenSilently()}`
        config.headers['x-scope-id'] = latestAuth.scopeId
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )
    /* oxlint-enable react/globals, react/immutability */
  }
  useEffect(() => {
    latestAuth.getAccessTokenSilently = getAccessTokenSilently
    latestAuth.scopeId = user?.currentScopeId || ''
  })

  // AxiosProvider exposes the same singleton to the hooks of @pubstack/http/api-client,
  // which read their instance from context instead of importing it.
  return (
    <Provider url={appConfig.BACKEND_ENDPOINT} options={options}>
      <AxiosProvider instance={api}>{children}</AxiosProvider>
    </Provider>
  )
}
